import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { MenuItem, TextField } from '@mui/material';
import { FiInfo } from 'react-icons/fi';
import { BsFillExclamationTriangleFill } from 'react-icons/bs';
import ToggleSwitch from '../../../../shared/components/ToggleSwitch';
import { UserService } from '../service';
import { toast } from 'react-toastify';
import { IProfileModal } from '../dto/IProfileModal';
import { ErrorToast } from '../../../../utils/toasters';
import { useTranslation } from 'react-i18next';
import { IGlobalPreference } from '../dto/IGlobalPreference';
import { actions } from '../../../../store/authentication/authRedux';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import { Languages } from '../../../../shared/utils/Languages';
import TooltipOverlay from '../../../../shared/components/tooltip/Tooltip';

function GlobalPreferencesModal({ show, closeModal, errorMessage }: IProfileModal) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { userType, company } = useSelector((state: IAllStates) => state.auth.user) || {};
    const isGlobalDiscovery = company?.package?.globalDiscovery;
    const isBrand = userType?.includes('brand');

    const [state, setState] = useState<IGlobalPreference>({
        isNotificationByEmailRequested: false,
        isNotificationInBrowserRequested: false,
        locale: 'en',
        isUnsubscribed: false,
    });

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>, name: string) => {
        setState(prev => ({ ...prev, [name]: name === 'isUnsubscribed' ? !event : event }));
    };

    useEffect(() => {
        if (show) {
            UserService.getGlobalPreference().then(data => {
                setState(data);
            });
        }
    }, [show]);

    const updatePreference = () => {
        UserService.updateGlobalPreference(state)
            .then(data => {
                toast.success('Successfully updated global preference');
                dispatch(actions.updateLanguage(data.locale));
                if (closeModal) {
                    closeModal();
                }
            })
            .catch(error => ErrorToast(error));
    };

    return (
        <Modal show={show} onHide={closeModal}>
            <Modal.Header>
                <Modal.Title>{t('brand.account.global_preferences')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group className="mt-2">
                    <TextField
                        value={state.locale}
                        size="small"
                        name="language"
                        label={t('general.language')}
                        onChange={event => setState({ ...state, locale: event.target.value })}
                        select
                        variant="outlined"
                        fullWidth
                    >
                        {Languages.map(lang => {
                            return (
                                <MenuItem value={lang.value} key={lang.value}>
                                    <div>{t(lang.label)}</div>
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    {errorMessage && (
                        <div className="text-danger text-center my-2">
                            <BsFillExclamationTriangleFill size={15} />
                            <span className="text-danger">{errorMessage}</span>
                        </div>
                    )}
                </Form.Group>

                <Row className="mt-4">
                    <Col>
                        <h6>{t('brand.account.global_preferences_modal.email_notifications')}</h6>
                    </Col>
                    {!isGlobalDiscovery && (
                        <Col className="col-12 d-flex justify-content-between my-2">
                            <div className="d-flex align-items-center gap-1">
                                {t('brand.account.global_preferences_modal.active_campaign_notifications')}
                                <TooltipOverlay
                                    content={t(
                                        'brand.account.global_preferences_modal.active_campaign_notifications_tooltip'
                                    )}
                                >
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </div>
                            <div>
                                <ToggleSwitch
                                    small={true}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        changeHandler(e, 'isNotificationByEmailRequested')
                                    }
                                    id="a"
                                    checked={state.isNotificationByEmailRequested}
                                    name="isNotificationByEmailRequested"
                                />
                            </div>
                        </Col>
                    )}
                    {!state.isNotificationByEmailRequested && (
                        <div className="d-flex justify-content-between my-2">
                            <div className="d-flex align-items-center gap-1">
                                {t('brand.account.global_preferences_modal.mentions_notifications')}
                                <TooltipOverlay
                                    content={t('brand.account.global_preferences_modal.mentions_notifications_tooltip')}
                                >
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </div>
                            <div>
                                <ToggleSwitch
                                    small={true}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        changeHandler(e, 'isNotificationByMentionRequested')
                                    }
                                    id="d"
                                    checked={state.isNotificationByMentionRequested}
                                    name="isNotificationByMentionRequested"
                                />
                            </div>
                        </div>
                    )}
                    {!isBrand && (
                        <div className="d-flex justify-content-between my-2">
                            <div className="d-flex align-items-center gap-1">
                                {t('brand.account.global_preferences_modal.campaign_notifications')}
                                <TooltipOverlay
                                    content={t('brand.account.global_preferences_modal.campaign_notifications_tooltip')}
                                >
                                    <FiInfo size={14} />
                                </TooltipOverlay>
                            </div>
                            <div>
                                <ToggleSwitch
                                    small={true}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                        changeHandler(e, 'isNotificationInBrowserRequested')
                                    }
                                    id="b"
                                    checked={state.isNotificationInBrowserRequested}
                                    name="isNotificationInBrowserRequested"
                                />
                            </div>
                        </div>
                    )}
                    <div className="d-flex justify-content-between my-2">
                        <div>{t('brand.account.global_preferences_modal.marketing_notifications')}</div>
                        <div>
                            <ToggleSwitch
                                small={true}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    changeHandler(e, 'isUnsubscribed')
                                }
                                name="isUnsubscribed"
                                id="c"
                                checked={!state.isUnsubscribed}
                            />
                        </div>
                    </div>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-primary" onClick={closeModal}>
                    {t('general.button.close')}
                </Button>
                <Button variant="primary" onClick={updatePreference}>
                    {t('general.button.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default GlobalPreferencesModal;
